import { ArrowDownIcon, ArrowUpIcon } from '@heroicons/react/24/outline'
import { LayoutLoading } from 'components/LayoutLoading'
import { TextHighlighter } from 'components/TextHighlighter'
import { useSelector } from 'react-redux'
import { capitalizeFirstLetter, removeComma, thousandSeperatorWithPrefix } from 'utils'

import { LOAN_QUERY_TYPE_OPTIONS, LoanQueryType } from './constants'
import { getBorrowerInitials, getLoanSearchParts, isServicingLoan } from './utils'

const formatLoanAmount = (value: any) => {
  if (value === null || value === undefined || value === '') return ''
  if (typeof value === 'string' && value.includes('$')) return value
  const amount = Number(removeComma(value))
  if (!Number.isFinite(amount)) return String(value)
  return thousandSeperatorWithPrefix(amount, '$', true)
}

export const LoanSearchResults = ({
  query,
  queryType,
  loans,
  total,
  loading,
  loadingMore = false,
  activeIndex,
  actionHint = 'to open',
  onSelect,
  onLoadMore,
  onQueryTypeChange,
  onActiveIndexChange,
}: {
  query: string
  queryType: LoanQueryType
  loans: Record<string, any>[]
  total: number
  loading: boolean
  loadingMore?: boolean
  activeIndex: number
  actionHint?: string
  onSelect: (loan: Record<string, any>) => void
  // Only a caller that pages supplies this; without it the list shows its first page and no
  // `Show more`, which is what the loan picker inside a modal wants.
  onLoadMore?: () => void
  onQueryTypeChange: (value: LoanQueryType) => void
  onActiveIndexChange: (index: number) => void
}) => {
  const loanGlobalStatus = useSelector((state: any) => state.loanGlobalStatus)
  const hasQuery = query.trim().length > 0
  const remaining = total - loans.length
  const canLoadMore = !!onLoadMore && !loading && remaining > 0

  const getStatusLabel = (loan: Record<string, any>) => {
    // A serviced loan's origination status is stale ("Funded"); its servicing status is the live one.
    const statusKey = isServicingLoan(loan)
      ? loan.servicingStatus || loan.loanStatus
      : loan.loanStatus || loan.servicingStatus
    if (!statusKey) return ''
    return loanGlobalStatus[statusKey] || capitalizeFirstLetter(String(statusKey).replace(/([A-Z])/g, ' $1').trim())
  }

  return (
    <div className="GlobalLoanSearch-results relative overflow-hidden rounded-xl border border-gray-200/80 bg-white shadow-2xl dark:border-slate-700">
      <LayoutLoading show={loading} />
      {!loading && !loans.length && hasQuery && (
        <div className="px-4 py-10 text-center text-sm text-gray-500">No loans found.</div>
      )}
      {!!loans.length && (
        <ul className="max-h-80 overflow-y-auto p-1.5">
          {loans.map((loan, index) => {
            const { loanNo, borrower, address } = getLoanSearchParts(loan)
            const isActive = index === activeIndex
            const amount = formatLoanAmount(loan.proposedLoanAmount)
            const statusLabel = getStatusLabel(loan)
            const isServicing = isServicingLoan(loan)

            return (
              <li key={loan.no || index}>
                <button
                  type="button"
                  className={`group w-full rounded-lg px-2.5 py-2.5 text-left transition-colors focus:outline-none ${
                    isActive ? 'bg-sky-50 dark:bg-sky-400/15' : 'hover:bg-gray-50'
                  }`}
                  onMouseDown={(event) => {
                    event.preventDefault()
                    onSelect(loan)
                  }}
                  onMouseEnter={() => onActiveIndexChange(index)}
                >
                  <div className="flex items-center gap-3">
                    <div className="flex h-9 w-9 shrink-0 items-center justify-center rounded-full bg-sky-100 text-xs font-semibold text-sky-700 dark:bg-sky-400/20 dark:text-sky-300">
                      {getBorrowerInitials(borrower || String(loanNo || '?'))}
                    </div>
                    <div className="min-w-0 flex-1">
                      {borrower ? (
                        <div className="truncate text-sm font-semibold text-gray-900">
                          <TextHighlighter searchText={query} text={borrower} />
                        </div>
                      ) : (
                        <div className="text-sm text-gray-400">No borrower</div>
                      )}
                      <div className="mt-0.5 flex min-w-0 items-center gap-1.5 text-xs text-gray-500">
                        {loanNo && (
                          <span className="shrink-0 font-medium tabular-nums text-gray-400">
                            <TextHighlighter searchText={query} text={`#${loanNo}`} />
                          </span>
                        )}
                        {loanNo && address && <span className="shrink-0 text-gray-300">·</span>}
                        {address && (
                          <span className="truncate">
                            <TextHighlighter searchText={query} text={address} />
                          </span>
                        )}
                      </div>
                    </div>
                    <div className="flex shrink-0 flex-col items-end justify-center self-stretch">
                      {amount && <div className="text-sm font-semibold text-gray-900">{amount}</div>}
                      {(isServicing || statusLabel) && (
                        <div className="mt-1 flex items-center gap-1">
                          {isServicing && (
                            <span className="inline-flex rounded-full bg-sky-50 px-2 py-0.5 text-[11px] font-medium text-sky-700 ring-1 ring-sky-100">
                              Servicing
                            </span>
                          )}
                          {statusLabel && (
                            <span className="inline-flex rounded-full bg-gray-100 px-2 py-0.5 text-[11px] font-medium text-gray-600">
                              {statusLabel}
                            </span>
                          )}
                        </div>
                      )}
                    </div>
                  </div>
                </button>
              </li>
            )
          })}
          {canLoadMore && (
            <li>
              <button
                type="button"
                className="w-full rounded-lg px-2.5 py-2 text-center text-xs font-medium text-sky-700 transition-colors hover:bg-sky-50 disabled:text-gray-400 dark:text-sky-300"
                // The input keeps focus so typing and the arrow keys still work after loading a
                // page, which a plain click would take away.
                onMouseDown={(event) => {
                  event.preventDefault()
                  onLoadMore?.()
                }}
                disabled={loadingMore}
              >
                {loadingMore ? 'Loading...' : 'Show more'}
              </button>
            </li>
          )}
        </ul>
      )}
      {hasQuery && (
        <div className="flex items-center justify-between gap-3 border-t border-gray-100 px-3 py-2.5 text-[11px] text-gray-400">
          <label className="flex items-center gap-1.5">
            <span className="shrink-0">Match</span>
            <select
              className="rounded-md border-0 bg-gray-100 py-0.5 pl-1.5 pr-5 text-[11px] text-gray-600 focus:outline-none focus:ring-1 focus:ring-sky-400"
              value={queryType}
              onChange={(event) => onQueryTypeChange(event.target.value as LoanQueryType)}
              onMouseDown={(event) => event.stopPropagation()}
            >
              {LOAN_QUERY_TYPE_OPTIONS.map((option) => (
                <option key={option} value={option}>
                  {capitalizeFirstLetter(option)}
                </option>
              ))}
            </select>
          </label>
          <div className="flex items-center gap-3">
            <span className="flex items-center gap-1">
              <ArrowUpIcon className="h-3 w-3" />
              <ArrowDownIcon className="h-3 w-3" />
              <span>to navigate</span>
            </span>
            <span className="flex items-center gap-1">
              <kbd className="rounded border border-gray-200 bg-gray-50 px-1 py-0.5 text-[10px] font-medium text-gray-500">
                ↵
              </kbd>
              <span>{actionHint}</span>
            </span>
            <span className="text-gray-300">
              {loans.length} of {total}
            </span>
          </div>
        </div>
      )}
    </div>
  )
}
